import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import './Modal.css'

/**
 * Modal komponent der kan bruges til at vise indhold i en overlay
 * @param {boolean} isOpen - Om modalen er åben eller lukket
 * @param {function} onClose - Funktion der kaldes når modalen lukkes
 * @param {string} title - Titel der vises i modal header
 * @param {React.ReactNode} children - Indhold der vises i modal body
 * @param {string} size - Størrelse på modalen ('small', 'medium', 'large', 'fullscreen')
 * @param {boolean} showCloseButton - Om close knappen skal vises
 * @param {boolean} closeOnOverlayClick - Om modalen lukkes når man klikker på overlay
 * @param {boolean} closeOnEscape - Om modalen lukkes når man trykker Escape
 * @param {string} className - Custom CSS klasse der skal tilføjes til modalen
 * @param {object} style - Custom inline styles der skal anvendes på modalen
 */
const Modal = ({
    isOpen = false,
    onClose,
    title,
    children,
    size = 'medium',
    showCloseButton = true,
    closeOnOverlayClick = true,
    closeOnEscape = true,
    className = '',
    style = {}
}) => {
    // Håndterer Escape key press
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (closeOnEscape && event.key === 'Escape' && isOpen) {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey)
            // Forhindrer body scroll når modal er åben
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey)
            document.body.style.overflow = 'unset'
        }
    }, [isOpen, closeOnEscape, onClose])

    // Håndterer overlay klik
    const handleOverlayClick = (event) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) {
            onClose()
        }
    }

    // Hvis modal ikke er åben, returner ingenting
    if (!isOpen) {
        return null
    }

    // Renderer modalen i modal-root div'et udenfor normal DOM hierarki
    const modalRoot = document.getElementById('modal-root')
    
    if (!modalRoot) {
        console.error('Modal root element not found')
        return null
    }

    const modalContent = (
        <div 
            className={`modal-overlay ${className}`}
            onClick={handleOverlayClick}
            style={style}
        >
            <div className={`modal-content ${size}`}>
                {/* Modal Header */}
                {(title || showCloseButton) && (
                    <div className="modal-header">
                        {title && <h2 className="modal-title">{title}</h2>}
                        {showCloseButton && (
                            <button 
                                className="modal-close-button"
                                onClick={onClose}
                                aria-label="Luk modal"
                            >
                                ×
                            </button>
                        )}
                    </div>
                )}
                
                {/* Modal Body */}
                <div className="modal-body">
                    {children}
                </div>
            </div>
        </div>
    )

    return ReactDOM.createPortal(modalContent, modalRoot)
}

export default Modal
